/**
 * A form section generated from a provider's field declarations.
 *
 * The provider says which fields it needs and of what kind; this renders them.
 * A secret is write-only: the API reports only whether one is stored, so the
 * field is empty whenever one is, says so, and an empty field keeps it.
 *
 * The server validates every field again — these checks only save a round trip.
 */

import { Input, PasswordInput, Select } from '@connect/web-shared';

import type { CredentialState, FieldSpec } from './types';

export type FieldValues = Record<string, string>;

/** Starting values: what is stored for a non-secret field, else its default. */
export function initialValues(
  specs: FieldSpec[],
  stored?: Record<string, string | CredentialState | undefined>,
): FieldValues {
  const out: FieldValues = {};
  for (const spec of specs) {
    const current = stored?.[spec.key];
    const value = typeof current === 'string' ? current : (current?.value ?? null);
    out[spec.key] = spec.kind === 'secret' ? '' : (value ?? spec.default ?? '');
  }
  return out;
}

/** Whether every required field has a value, counting a stored secret as one. */
export function requiredFilled(
  specs: FieldSpec[],
  values: FieldValues,
  stored?: Record<string, CredentialState>,
): boolean {
  return specs.every(
    (spec) =>
      !spec.required ||
      Boolean(values[spec.key]?.trim()) ||
      (spec.kind === 'secret' && Boolean(stored?.[spec.key]?.set)),
  );
}

/** The first local problem with a field's value, if any. */
function problemWith(spec: FieldSpec, value: string): string | undefined {
  if (!value || spec.kind === 'secret' || !spec.pattern) return undefined;
  return new RegExp(`^(?:${spec.pattern})$`).test(value.trim())
    ? undefined
    : `This does not look like a valid ${spec.label.toLowerCase()}.`;
}

export function fieldsValid(specs: FieldSpec[], values: FieldValues): boolean {
  return specs.every((spec) => !problemWith(spec, values[spec.key] ?? ''));
}

export function ProviderFields({
  specs,
  values,
  onChange,
  stored,
  errors,
  errorPrefix,
  disabled,
}: {
  specs: FieldSpec[];
  values: FieldValues;
  onChange: (next: FieldValues) => void;
  /** What the API reports as stored, for a service that already exists. */
  stored?: Record<string, CredentialState>;
  /** Field errors from the API, keyed `<errorPrefix>.<key>`. */
  errors?: Record<string, string>;
  errorPrefix: string;
  disabled?: boolean;
}) {
  if (!specs.length) return null;
  const set = (key: string, value: string) => onChange({ ...values, [key]: value });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {specs.map((spec) => {
        const value = values[spec.key] ?? '';
        const error = errors?.[`${errorPrefix}.${spec.key}`] ?? problemWith(spec, value);
        const hint = spec.help ?? undefined;

        if (spec.kind === 'secret') {
          // Somebody who may not edit sees no secret field at all: there is
          // nothing to show, and an empty password box reads as "none set".
          if (disabled) return null;
          const isStored = Boolean(stored?.[spec.key]?.set);
          return (
            <PasswordInput
              key={spec.key}
              label={spec.label}
              required={spec.required && !isStored}
              value={value}
              error={error}
              autoComplete="new-password"
              placeholder={isStored ? 'Stored — leave empty to keep it' : (spec.placeholder ?? undefined)}
              hint={
                isStored
                  ? 'Stored encrypted and never shown. Type a new one only to replace it.'
                  : (hint ?? 'Stored encrypted, and never shown again.')
              }
              onChange={(event) => set(spec.key, event.target.value)}
            />
          );
        }

        if (spec.kind === 'select') {
          return (
            <Select
              key={spec.key}
              label={spec.label}
              required={spec.required}
              options={spec.options}
              value={value}
              error={error}
              hint={hint}
              disabled={disabled}
              onChange={(event) => set(spec.key, event.target.value)}
            />
          );
        }

        return (
          <Input
            key={spec.key}
            label={spec.label}
            required={spec.required}
            type={spec.kind === 'number' ? 'number' : spec.kind === 'url' ? 'url' : 'text'}
            value={value}
            error={error}
            hint={hint}
            placeholder={spec.placeholder ?? undefined}
            maxLength={spec.max_length ?? undefined}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => set(spec.key, event.target.value)}
          />
        );
      })}
    </div>
  );
}
